import { AnimatePresence, motion } from "framer-motion";
import { type FC, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { z } from "zod";

import { useOverlay } from "../../hooks/useOverlay";
import { formatTokensCompact } from "../../utils/currency";
import { HudStatusPill, type HudStatusTone } from "../common/HudStatusPill";
import { HudSparkline, type HudSparklineTone } from "./HudSparkline";
import { ModelChip } from "./ModelChip";

// ---------------------------------------------------------------------------
// Wake-growth distribution — opened from the bento header widget. Same
// Silhouette as BurnRateModal: portal + overlay + corner-bracketed panel
// With grid bg + scanlines + emerald accent. Per-actor mean/p50/p95/max
// Stats with HudSparkline of recent persisted-context deltas.
//
// Data only fetches when `open` flips true so closed modals don't poll.
// ---------------------------------------------------------------------------

const actorGrowthSchema = z.object({
  actor_instance_id: z.string(),
  display_name: z.string().nullable(),
  max_growth: z.number(),
  mean_growth: z.number(),
  model_ref: z.string().nullable(),
  p50_growth: z.number(),
  p95_growth: z.number(),
  pair_count: z.number(),
  recent_deltas: z.array(z.number()),
  total_growth: z.number(),
});
const distributionResponseSchema = z.object({
  actors: z.array(actorGrowthSchema),
  overall_max_growth: z.number(),
  overall_mean_growth: z.number(),
  overall_p50_growth: z.number(),
  overall_p95_growth: z.number(),
  since: z.string(),
  total_pairs: z.number(),
  window_ms: z.number(),
});
type ActorRow = z.infer<typeof actorGrowthSchema>;
type DistributionResponse = z.infer<typeof distributionResponseSchema>;

const WINDOW_OPTIONS: { label: string; ms: number }[] = [
  { label: "1H", ms: 60 * 60 * 1000 },
  { label: "6H", ms: 6 * 60 * 60 * 1000 },
  { label: "24H", ms: 24 * 60 * 60 * 1000 },
];

const REFRESH_INTERVAL_MS = 30_000;
const NOMINAL_THRESHOLD = 25_000;

interface DecisionHint {
  label: string;
  tone: HudStatusTone;
  sparklineTone: HudSparklineTone;
}

function pickDecisionHint(meanGrowth: number): DecisionHint {
  const utilization = meanGrowth / NOMINAL_THRESHOLD;
  if (utilization < 0.4) {
    return { label: "HEADROOM", sparklineTone: "success", tone: "success" };
  }
  if (utilization < 0.8) {
    return { label: "NORMAL", sparklineTone: "info", tone: "info" };
  }
  if (utilization < 1.2) {
    return { label: "SATURATING", sparklineTone: "warning", tone: "warning" };
  }
  return { label: "THRASHING", sparklineTone: "danger", tone: "danger" };
}

const STAT_LABELS = ["MEAN", "P50", "P95", "MAX"] as const;

const overlayMotion = {
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  initial: { opacity: 0 },
  transition: { duration: 0.15 },
};

const panelMotion = {
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.97, y: 8 },
  initial: { opacity: 0, scale: 0.97, y: 8 },
  transition: {
    damping: 22,
    mass: 0.8,
    stiffness: 260,
    type: "spring" as const,
  },
};

const WindowTabs: FC<{ current: number; onChange: (ms: number) => void }> = ({
  current,
  onChange,
}) => (
  <div className="flex gap-1 font-mono text-[10px] uppercase tracking-[0.2em]">
    {WINDOW_OPTIONS.map((opt) => {
      const active = opt.ms === current;
      return (
        <button
          key={opt.ms}
          className={`border px-2 py-1 transition-colors ${
            active
              ? "border-emerald-500/60 bg-emerald-400/10 text-emerald-700 dark:border-emerald-400/60 dark:text-emerald-300"
              : "border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700 dark:border-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-300"
          }`}
          onClick={() => onChange(opt.ms)}
          type="button"
        >
          [ {opt.label} ]
        </button>
      );
    })}
  </div>
);

const StatCell: FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
      [ {label} ]
    </span>
    <span className="font-mono text-base font-semibold tabular-nums text-slate-800 dark:text-slate-200">
      {formatTokensCompact(value)}
    </span>
  </div>
);

const ActorRowEl: FC<{ row: ActorRow; index: number }> = ({ row, index }) => {
  const hint = useMemo(
    () => pickDecisionHint(row.mean_growth),
    [row.mean_growth],
  );
  return (
    <motion.div
      animate={{ opacity: 1, x: 0 }}
      className="grid grid-cols-12 items-center gap-4 border-b border-emerald-500/20 px-3 py-3 last:border-b-0 dark:border-emerald-400/10"
      initial={{ opacity: 0, x: -16 }}
      transition={{
        damping: 22,
        delay: Math.min(index * 0.04, 0.32),
        stiffness: 260,
        type: "spring",
      }}
    >
      <div className="col-span-5 flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          {row.model_ref ? <ModelChip modelRef={row.model_ref} /> : null}
          <span className="font-mono text-sm text-slate-800 dark:text-slate-200">
            {row.display_name ?? row.actor_instance_id.slice(0, 8)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <HudStatusPill label={hint.label} tone={hint.tone} />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            n={row.pair_count}
          </span>
        </div>
      </div>
      <div className="col-span-7 flex flex-col gap-2">
        <div className="grid grid-cols-4 gap-3">
          {[
            row.mean_growth,
            row.p50_growth,
            row.p95_growth,
            row.max_growth,
          ].map((value, idx) => {
            const label = STAT_LABELS[idx];
            if (label === undefined) {
              return null;
            }
            return <StatCell key={label} label={label} value={value} />;
          })}
        </div>
        <HudSparkline
          tone={hint.sparklineTone}
          values={row.recent_deltas ?? []}
          width={320}
        />
      </div>
    </motion.div>
  );
};

const CornerBracket: FC<{ position: "tl" | "tr" | "bl" | "br" }> = ({
  position,
}) => {
  const map: Record<typeof position, string> = {
    bl: "bottom-1 left-1 border-b border-l",
    br: "bottom-1 right-1 border-b border-r",
    tl: "top-1 left-1 border-l border-t",
    tr: "top-1 right-1 border-r border-t",
  };
  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute z-10 h-2 w-2 ${map[position]} border-emerald-400/40`}
    />
  );
};

const DistributionResult: FC<{ data: DistributionResponse }> = ({ data }) => (
  <div>
    <div className="border-b border-emerald-500/25 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:border-emerald-400/15">
      [ FLEET ] mean {formatTokensCompact(data.overall_mean_growth)} · P50{" "}
      {formatTokensCompact(data.overall_p50_growth)} · P95{" "}
      {formatTokensCompact(data.overall_p95_growth)} · MAX{" "}
      {formatTokensCompact(data.overall_max_growth)} · N={data.total_pairs}
    </div>
    <div>
      {data.actors.map((row, idx) => (
        <ActorRowEl key={row.actor_instance_id} index={idx} row={row} />
      ))}
    </div>
  </div>
);

interface WakeGrowthModalProps {
  open: boolean;
  onClose: () => void;
}

export const WakeGrowthModal: FC<WakeGrowthModalProps> = ({
  open,
  onClose,
}) => {
  useOverlay(open, onClose);
  const initialMs = WINDOW_OPTIONS[0]?.ms ?? 60 * 60 * 1000;
  const [windowMs, setWindowMs] = useState<number>(initialMs);
  const [data, setData] = useState<DistributionResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const fetchDistribution = async () => {
      try {
        const res = await fetch(
          `/user/instances/growth-distribution?windowMs=${windowMs}`,
          { credentials: "include" },
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const json = distributionResponseSchema.parse(await res.json());
        if (!cancelled) {
          setData(json);
          setErrorMessage(null);
          setLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            error instanceof Error ? error.message : String(error),
          );
          setLoading(false);
        }
      }
    };
    setLoading(true);
    void fetchDistribution();
    const interval = setInterval(fetchDistribution, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, windowMs]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          {...overlayMotion}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 backdrop-blur-sm md:p-6"
          onClick={onClose}
        >
          <motion.div
            {...panelMotion}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl overflow-hidden border border-emerald-500/40 bg-white shadow-[0_0_0_1px_rgba(16,185,129,0.08),0_40px_80px_-20px_rgba(16,185,129,0.35)] dark:border-emerald-400/30 dark:bg-slate-950"
            style={{
              backgroundImage:
                "linear-gradient(rgba(16,185,129,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.04) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          >
            <CornerBracket position="tl" />
            <CornerBracket position="tr" />
            <CornerBracket position="bl" />
            <CornerBracket position="br" />

            <div
              className="pointer-events-none absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(0deg, rgba(16,185,129,1) 0 1px, transparent 1px 3px)",
              }}
            />

            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent" />

            <div className="relative flex items-center justify-between border-b border-emerald-500/25 px-6 py-4 dark:border-emerald-400/15">
              <div>
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700 dark:text-emerald-400/70">
                  [ WAKE GROWTH ]
                </p>
                <p className="mt-1 font-mono text-[11px] tracking-tight text-slate-500">
                  persisted-context delta wake-to-wake · cost-aligned signal
                </p>
              </div>
              <div className="flex items-center gap-3">
                <WindowTabs current={windowMs} onChange={setWindowMs} />
                <button
                  type="button"
                  onClick={onClose}
                  className="group relative flex h-7 w-7 items-center justify-center border border-emerald-500/40 font-mono text-[12px] text-emerald-700/90 hover:border-emerald-500/70 hover:text-emerald-700 dark:border-emerald-400/30 dark:text-emerald-300/80 dark:hover:border-emerald-400/60 dark:hover:text-emerald-300"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="relative max-h-[72vh] overflow-y-auto px-2 py-3 md:px-3">
              {loading && (
                <div className="px-3 py-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                  [ LOADING ]
                </div>
              )}
              {errorMessage && !loading && (
                <div className="px-3 py-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-rose-700 dark:text-rose-500">
                  [ ERROR · {errorMessage} ]
                </div>
              )}
              {!loading &&
                !errorMessage &&
                data &&
                data.actors.length === 0 && (
                  <div className="px-3 py-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                    [ NO WAKE PAIRS IN WINDOW ]
                  </div>
                )}
              {!loading && !errorMessage && data && data.actors.length > 0 && (
                <DistributionResult data={data} />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
